import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

// Themed replacement for a native <select>: the OS option popup can't be
// styled and ignores dark mode entirely, so the pickers on the board render
// their own in-theme menu card instead. Pick-only (no persistent value) —
// the trigger label is whatever the caller passes.
//
// The menu floats above the page (a portal, fixed to the trigger), so a card
// that clips its contents can never hide it (Nic 2026-10-23: CM Assignment's
// Claims Manager list was cut off inside its tile). It opens upward when
// there's more room above, and follows the trigger on scroll and resize.
const GUTTER = 8;
const MENU_MAX = 224; // max-h-56

const PickerMenu = ({
  label,
  options, // [{ value, label, hint? }] — hint renders right-aligned, never truncated
  onPick,
  disabled = false,
  triggerClassName = '',
  title,
  emptyLabel = 'No one available',
  // Optional overrides (the board's compact look is the default).
  menuClassName = 'rounded-lg',
  itemClassName = 'px-2.5 py-1.5 text-xs',
  selectedValue,
  // The menu is at least this wide (px), however small the trigger: a tiny
  // chip opened a menu that cut its choices off with "..." (Nic 2026-10-24).
  menuMinWidth = 176,
  // How tall the menu may grow before it scrolls (px).
  menuMaxHeight = MENU_MAX,
}) => {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (
        rootRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Where the menu sits: under the trigger, or above it when there's more
  // room there; never past the window's edges.
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return undefined;
    }
    const measure = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const vw = document.documentElement.clientWidth;
      const vh = window.innerHeight;
      const width = Math.min(Math.max(r.width, menuMinWidth), vw - GUTTER * 2);
      const left = Math.min(Math.max(GUTTER, r.left), vw - width - GUTTER);
      const below = vh - r.bottom - GUTTER;
      const above = r.top - GUTTER;
      const want = Math.min(
        menuMaxHeight,
        menuRef.current?.scrollHeight || menuMaxHeight,
      );
      const up = below < want && above > below;
      setPlace({
        position: 'fixed',
        left,
        width,
        maxHeight: Math.max(
          120,
          Math.min(menuMaxHeight, (up ? above : below) - 4),
        ),
        ...(up ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, menuMinWidth, menuMaxHeight]);

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        title={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-1.5 disabled:opacity-50 ${triggerClassName}`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          size={11}
          className={`shrink-0 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open
        ? createPortal(
            // The card clips (rounded, overflow hidden) and an inner list
            // scrolls with a thin, arrowless bar, so a long list never shows
            // a square scrollbar poking past the corners (Nic 2026-10-25).
            <div
              ref={menuRef}
              role="listbox"
              style={place || { position: 'fixed', visibility: 'hidden' }}
              className={`z-[1200] flex flex-col overflow-hidden ${menuClassName} bg-white shadow-[0_4px_20px_rgba(15,23,42,0.15)] ring-1 ring-slate-200/80 dark:!bg-surface-elevated dark:ring-white/10`}
            >
              <div className="min-h-0 flex-1 overflow-y-auto py-1 [scrollbar-color:rgba(148,163,184,0.55)_transparent] [scrollbar-width:thin]">
                {options.length === 0 ? (
                  <div className="px-2.5 py-1.5 text-xs text-slate-400 dark:text-slate-500">
                    {emptyLabel}
                  </div>
                ) : (
                  options.map((o) => (
                    <button
                      key={String(o.value)}
                      type="button"
                      role="option"
                      aria-selected={
                        selectedValue !== undefined && o.value === selectedValue
                      }
                      onClick={() => {
                        setOpen(false);
                        onPick(o.value);
                      }}
                      className={`flex w-full items-center ${itemClassName} text-left font-medium hover:bg-slate-50 dark:hover:bg-white/10 ${
                        selectedValue !== undefined && o.value === selectedValue
                          ? 'text-slate-900 dark:text-white'
                          : 'text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      <span className="truncate">{o.label}</span>
                      {o.hint ? (
                        <span className="ml-auto shrink-0 pl-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                          {o.hint}
                        </span>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default PickerMenu;
